import React, { useEffect, useState } from 'react';
import { adminManagementApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import { MdEdit, MdClose, MdSearch, MdLocationOn } from 'react-icons/md';
import './AdminLocationManagement.css';

function AdminLocationManagement() {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [allLocationsEnabled, setAllLocationsEnabled] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      const [adminsResponse, locationsResponse] = await Promise.all([
        adminManagementApi.getAllAdmins(),
        adminManagementApi.getAllLocations()
      ]);

      // Parse the response if it's a string (backend returning stringified JSON)
      const adminsParsed = typeof adminsResponse === 'string' ? JSON.parse(adminsResponse) : adminsResponse;
      const locationsParsed = typeof locationsResponse === 'string' ? JSON.parse(locationsResponse) : locationsResponse;

      // Extract the data array from the ResponseHandler wrapper
      const adminsData = adminsParsed?.data || [];
      const locationsData = locationsParsed?.data || [];


      // Ensure we always have arrays
      setUsers(Array.isArray(adminsData) ? adminsData : []);
      setLocations(Array.isArray(locationsData) ? locationsData : []);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch data');
      toast.error('Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);

    // Initialize selected locations
    if (user.assignedLocations === '*') {
      setAllLocationsEnabled(true);
      setSelectedLocations([...locations]);
    } else if (user.assignedLocations) {
      const assigned = user.assignedLocations.split(',').map(l => l.trim());
      setSelectedLocations(assigned);
      setAllLocationsEnabled(false);
    } else {
      setSelectedLocations(user.location ? [user.location] : []);
      setAllLocationsEnabled(false);
    }

    setShowModal(true);
    setLocationSearch('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setSelectedLocations([]);
    setAllLocationsEnabled(false);
    setLocationSearch('');
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations(prev => {
      const isSelected = prev.includes(location);
      if (isSelected) {
        return prev.filter(l => l !== location);
      } else {
        return [...prev, location];
      }
    });
  };

  const handleAllLocationsToggle = () => {
    const newValue = !allLocationsEnabled;
    setAllLocationsEnabled(newValue);
    if (newValue) {
      setSelectedLocations([...locations]);
    }
  };

  const handleSaveLocations = async () => {
    if (!editingUser) return;

    try {
      setIsLoading(true);

      let assignedLocationsString;
      if (allLocationsEnabled) {
        assignedLocationsString = '*';
      } else {
        assignedLocationsString = selectedLocations.join(',');
      }

      await adminManagementApi.updateAdminLocations(editingUser.id, assignedLocationsString);

      toast.success(`Locations updated successfully for ${editingUser.firstName} ${editingUser.secondName}`);
      handleCloseModal();
      await fetchData();
    } catch (err) {
      console.error('Error updating locations:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update locations';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const getAssignedLocationsDisplay = (user) => {
    if (user.assignedLocations === '*') {
      return <span className="all-locations-badge">All Locations</span>;
    } else if (user.assignedLocations) {
      const locs = user.assignedLocations.split(',').map(l => l.trim());
      return locs.slice(0, 3).map((loc, idx) => (
        <span key={idx} className="location-chip">{loc}</span>
      )).concat(
        locs.length > 3 ? <span key="more" className="location-chip more">+{locs.length - 3}</span> : []
      );
    } else {
      return <span className="location-chip">{user.location}</span>;
    }
  };

  const filteredUsers = users.filter(user => {
    // Exclude REGISTERED_USER role
    if (user.role === 'REGISTERED_USER') {
      return false;
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.secondName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.empId?.toLowerCase().includes(searchLower) ||
      user.department?.toLowerCase().includes(searchLower)
    );
  });

  const filteredLocations = locations.filter(loc =>
    loc.toLowerCase().includes(locationSearch.toLowerCase())
  );

  if (error && users.length === 0) {
    return (
      <div className="admin-content admin-location-management">
        <div className="page-header-compact">
          <div className="header-left-compact">
            <h2 className="page-title-compact">
              <MdLocationOn className="title-icon" />
              User Location Management
            </h2>
          </div>
        </div>
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button className="retry-button" onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content admin-location-management">
      {/* Compact Header with Search */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdLocationOn className="title-icon" />
            User Location Management
          </h2>
        </div>
        <div className="header-filters-compact">
          <div className="search-compact">
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading && users.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      ) : (
        <div className="table-container">
          {filteredUsers.length === 0 ? (
            <div className="no-users">
              <p>{searchTerm ? 'No users found matching your search' : 'No users found'}</p>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Department</th>
                  <th>Primary Location</th>
                  <th>Assigned Locations</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="emp-id">{user.empId}</td>
                    <td className="user-name">{user.firstName} {user.secondName}</td>
                    <td className="user-email">{user.email}</td>
                    <td>{user.department || '-'}</td>
                    <td>{user.location}</td>
                    <td className="locations-cell">
                      <div className="locations-display">
                        {getAssignedLocationsDisplay(user)}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-edit"
                        onClick={() => handleEditClick(user)}
                        title="Edit Locations"
                      >
                        <MdEdit />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Edit Locations Modal */}
      {showModal && editingUser && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Edit Location Access</h2>
                <p className="modal-subtitle">
                  {editingUser.firstName} {editingUser.secondName} ({editingUser.email})
                </p>
              </div>
              <button className="btn-close" onClick={handleCloseModal}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body">
              {/* All Locations Toggle */}
              <div className="all-locations-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={allLocationsEnabled}
                    onChange={handleAllLocationsToggle}
                  />
                  <span>Enable Access to All Locations (*)</span>
                </label>
                {allLocationsEnabled && (
                  <p className="info-text">
                    This user will have access to all current and future locations.
                  </p>
                )}
              </div>

              {/* Location Selection */}
              {!allLocationsEnabled && (
                <>
                  <div className="location-search">
                    <MdSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search locations..."
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                    />
                  </div>

                  <div className="locations-grid">
                    {filteredLocations.length === 0 ? (
                      <p className="no-results">No locations found</p>
                    ) : (
                      filteredLocations.map(location => (
                        <button
                          key={location}
                          className={`location-badge ${selectedLocations.includes(location) ? 'selected' : ''}`}
                          onClick={() => handleLocationToggle(location)}
                        >
                          {location}
                          {selectedLocations.includes(location) && (
                            <span className="check-icon">✓</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>

                  <div className="selection-summary">
                    <span>{selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''} selected</span>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal} disabled={isLoading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveLocations} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLocationManagement;
